/**
 * アンダーグロー用の Canvas グラデーションテクスチャ生成（外部画像なし）。
 * three.js 非依存 — HTMLCanvasElement を返すだけなので、呼び出し側
 * （board.js）が THREE.CanvasTexture に包む。
 *
 * document が無い環境（node / vitest）では全関数が null を返す no-op。
 * 呼び出し側は null ならグローの構築自体をスキップする。
 *
 * キャンバスの向きは「北が上の地図」: 描画側がプレーンを rotateX(-π/2) で
 * 寝かせるため、キャンバス上端 → ワールド -z（row 0 / 先手ブルー側）、
 * 下端 → +z（後手レッド側）、左 → -x、右 → +x に対応する。
 */

function makeCanvas(w, h) {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return canvas;
}

/**
 * 床グロー用テクスチャ（1プレイヤーぶんの光のローブ）。
 * player = 0: 上寄り（-z / row 0 側）にブルーの radial ローブ、
 * player = 1: 下寄り（+z 側）にレッドのローブ。中心から外周へ向かって
 * フェードし、辺縁は完全透明。2 枚を加算合成で重ねると中央で自然に混ざる。
 * 戻り値: HTMLCanvasElement | null（document なし）。
 */
export function createFloorGlowCanvas(player, { size = 256, color } = {}) {
  const canvas = makeCanvas(size, size);
  if (!canvas) return null;
  const ctx = canvas.getContext('2d');
  const c = color ?? (player === 1 ? '#ff6b6b' : '#4da3ff');
  const cx = size / 2;
  const cy = size * (player === 1 ? 0.68 : 0.32); // 自陣側へ寄せる
  const r = size * 0.62;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  grad.addColorStop(0, hexToRgba(c, 1));
  grad.addColorStop(0.45, hexToRgba(c, 0.55));
  grad.addColorStop(0.8, hexToRgba(c, 0.12));
  grad.addColorStop(1, hexToRgba(c, 0));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  return canvas;
}

/**
 * 側面ストリップ用テクスチャ。長手方向の色（単色またはグラデーション）を
 * 塗ったあと、土台の縁から外側へ向かうアルファフェードを重ねる。
 *   colorFrom / colorTo … 色（colorTo 省略で単色）
 *   colorAxis           … 色の変化方向 'x'（左→右）| 'y'（上→下）
 *   fadeFrom            … アルファが 1 の辺（土台に接する側）'top'|'bottom'|'left'|'right'
 * 戻り値: HTMLCanvasElement | null（document なし）。
 */
export function createStripGlowCanvas({
  colorFrom = '#4da3ff',
  colorTo = null,
  colorAxis = 'x',
  fadeFrom = 'bottom',
  w = 128,
  h = 32,
} = {}) {
  const canvas = makeCanvas(w, h);
  if (!canvas) return null;
  const ctx = canvas.getContext('2d');

  if (colorTo) {
    const grad =
      colorAxis === 'y'
        ? ctx.createLinearGradient(0, 0, 0, h)
        : ctx.createLinearGradient(0, 0, w, 0);
    grad.addColorStop(0, colorFrom);
    grad.addColorStop(1, colorTo);
    ctx.fillStyle = grad;
  } else {
    ctx.fillStyle = colorFrom;
  }
  ctx.fillRect(0, 0, w, h);

  // 土台の縁（fadeFrom の辺）で最も明るく、外側の辺で完全透明になるフェード
  const [x0, y0, x1, y1] =
    fadeFrom === 'top'
      ? [0, 0, 0, h]
      : fadeFrom === 'bottom'
        ? [0, h, 0, 0]
        : fadeFrom === 'left'
          ? [0, 0, w, 0]
          : [w, 0, 0, 0]; // 'right'
  const fade = ctx.createLinearGradient(x0, y0, x1, y1);
  fade.addColorStop(0, 'rgba(255,255,255,1)');
  fade.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.globalCompositeOperation = 'destination-in';
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';
  return canvas;
}

/** '#rrggbb' → 'rgba(r,g,b,a)'。 */
function hexToRgba(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${a})`;
}
